import { useCallback, useEffect, useMemo, useState } from "react";
import type { AssignmentProposal } from "@models/ISchema";
import { splitProposalsByTab } from "@services/collections/assignment-proposal/assignmentProposalListAdapters";
import {
  useAssignmentProposals,
  type AssignmentProposalsStatus,
} from "@services/collections/assignment-proposal/assignmentProposalListServices";

export interface ProposalListState {
  status: AssignmentProposalsStatus;
  proposals: AssignmentProposal[];
}

interface GroupProposalListsParams {
  /** The lists are requested only while the Drawer is open. */
  isOpen: boolean;
  linkId: string | null;
  scenarioId: string;
  originId: string;
  /** The user sends proposals: "Enviadas" and "Borradores". */
  canSubmit: boolean;
  /** The user reviews proposals: "Por revisar". */
  canReview: boolean;
  /** Whether "Por revisar" of a link has proposals, once it loads (the header dot uses it). */
  onReviewLoaded?: (linkId: string, hasPending: boolean) => void;
}

/**
 * The lists of the proposals Drawer for one link. Each one is requested only for the role that
 * needs it, every time the Drawer opens and every time the link changes, never reused: its change
 * summary is computed against the schedule as it is at that moment.
 */
export const useGroupProposalLists = ({
  isOpen,
  linkId,
  scenarioId,
  originId,
  canSubmit,
  canReview,
  onReviewLoaded,
}: GroupProposalListsParams) => {
  const ownList = useAssignmentProposals();
  const reviewList = useAssignmentProposals({ forReview: true });
  const [ownAttempt, setOwnAttempt] = useState(0);
  const [reviewAttempt, setReviewAttempt] = useState(0);
  const loadOwn = ownList.load;
  const loadReview = reviewList.load;

  useEffect(() => {
    if (!isOpen || !linkId || !canSubmit) return;

    loadOwn({ scenarioId, originId, linkId });
    // `ownAttempt` asks again on a retry and after deleting a draft.
  }, [isOpen, linkId, scenarioId, originId, canSubmit, ownAttempt, loadOwn]);

  useEffect(() => {
    if (!isOpen || !linkId || !canReview) return undefined;

    let isActive = true;

    loadReview({ scenarioId, originId, linkId }).then(loaded => {
      if (isActive && loaded) onReviewLoaded?.(linkId, loaded.length > 0);
    });

    return () => {
      isActive = false;
    };
    // `reviewAttempt` asks again on a retry and after a rejection.
  }, [isOpen, linkId, scenarioId, originId, canReview, reviewAttempt, loadReview]);

  const { sent, drafts } = useMemo(
    () => splitProposalsByTab(ownList.proposals),
    [ownList.proposals],
  );
  // What the user has to review is in "Por revisar", not in "Enviadas".
  const sentNotToReview = useMemo(() => {
    const toReview = new Set(reviewList.proposals.map(proposal => proposal.id));

    return sent.filter(proposal => !toReview.has(proposal.id));
  }, [sent, reviewList.proposals]);
  // "Enviadas" waits for "Por revisar", so nothing moves from one tab to the other on screen.
  const isReviewPending =
    canReview && (reviewList.status === "idle" || reviewList.status === "loading");

  const reloadOwn = useCallback(() => setOwnAttempt(attempt => attempt + 1), []);
  const reloadReview = useCallback(() => setReviewAttempt(attempt => attempt + 1), []);

  const sentList: ProposalListState = {
    status: ownList.status === "loaded" && isReviewPending ? "loading" : ownList.status,
    proposals: sentNotToReview,
  };
  const draftList: ProposalListState = { status: ownList.status, proposals: drafts };
  const reviewListState: ProposalListState = {
    status: reviewList.status,
    proposals: reviewList.proposals,
  };

  return { sent: sentList, drafts: draftList, review: reviewListState, reloadOwn, reloadReview };
};
